import axios from "axios";
import _ from 'lodash';
import {axiosCatchHandler} from "../sharedGlobal";

const pages = {
  namespaced: true,
  state: {
    pages: null,
    status: 'Saved',
    editing: false,
    dataType: 'live',
    pageWidthPx: 0
  },
  mutations: {
    setState(state, pages){
      state.pages = _.orderBy(pages, ['position'], ['asc']);
    },
    async updateLayout(state, {pageIndex, layout}){
      state.pages[pageIndex].layout = layout;
    },
    toggleEditing(state, payload){
      if(payload !== undefined){
        state.editing = payload;
      } else {
        state.editing = !state.editing;
      }
    },
    toggleDataType(state, payload){
      state.dataType = payload;
    },
  },
  actions: {
    getReportPages: ({commit}, {type, id}) => {
      commit('setState', null);
      return axios.get(`/ajax/report-pages/${type}/${id}`)
        .then(({ data }) => commit('setState', data));
    },
    getPreviewReportPages: ({commit}, {uuid}) =>
      axios.post('/ajax/pages/preview', {uuid: uuid})
        .then(({ data }) => commit('setState', data)),
    addPage: ({state, commit}, {reportId, isTemplate}) => {
      // Count current pages:
      var position = 1;
      for (const page in state.pages){
        position++;
      }

      var requestData = {
        report_id: reportId,
        is_template: isTemplate,
        position: position
      };

      return axios.post('/ajax/reports/builder/add-page', { data: requestData })
        .then(({ data }) => commit('setState', data));
    },
    async savePages({state, dispatch}, pages = state.pages) {
      state.status = 'Saving';
      await dispatch('debounceSavePages');
    },
    debounceSavePages: _.debounce( ({state, dispatch}, pages = state.pages) => {
      dispatch('savePagesRequest');
    }, 2000),
    savePagesRequest: ({state, commit, dispatch, rootState}, pages = state.pages) => {
      return new Promise((resolve) => {
        if(state.editing){
          dispatch('savePages');
          resolve();
          return false;
        }

        state.status = 'Saving';
        let previewPages = [];

        // Take a clone of the state as we don't want to make changes to the actual state
        let clonedPages = _.cloneDeep(pages);

        _.forEach(clonedPages, page => {
          // Fetch the gridstack element
          let grid = document.querySelector('#p'+page.id+'-grid');
          if (grid) {
            grid = grid.gridstack;
          } else {
            dispatch('savePages');
            console.log('No grid found for page '+page.id);
            return false;
          }
          // grid.save returns an array of all the widgets
          let widgets = grid.save();
          // If the pages are not for a template, prep a copy of the pages for previewing
          if(!rootState.report.is_template){
            let preview = _.cloneDeep(page);
            preview.layout = _.cloneDeep(widgets);
            previewPages.push(preview);
          }

          for(const widget in widgets){
            if (widgets[widget].attributes.type &&
              (
                widgets[widget].attributes.type.includes(`chart`) ||
                widgets[widget].attributes.type === 'table' ||
                widgets[widget].attributes.type === 'stat-boxes'
              )
            ){
              widgets[widget].content = `<div id="${widgets[widget].id}"><div class="widget-loading"><i class="fa fa-btn fa-spinner fa-spin"></i></div></div>`;
            }
            // Clear the widgets data unless its a ga/gsc widget or its been frozen
            if (widgets[widget].type !== 'ga' && widgets[widget].type !== 'gsc' && !widgets[widget].frozen){
              widgets[widget].data = null;
            }

            /**
             * Changes appear to have been made for the GA4 API dimension names
             * This is a workaround to get the correct dimensions.
             * TODO: Remove this workaround when all references replaced in DB.
             */
            if (widgets[widget]?.attributes?.options?.dimension === 'defaultChannelGrouping') {
              widgets[widget].attributes.options.dimension = 'sessionDefaultChannelGrouping';
            }
            if (widgets[widget]?.attributes?.options?.filter?.dimension === 'medium') {
              widgets[widget].attributes.options.filter.dimension = 'sessionMedium';
            }
          }

          page.layout = widgets;
        });

        let payload = {
          report_id: rootState.report.report.id,
          type: rootState.report.type,
          pages: clonedPages,
          preview_pages: previewPages
        };

        axios.post('/ajax/reports/builder/save-pages',{
            data: payload
          }).then(() => {
            state.status = 'Saved';
            resolve();
          });
      });
    },
    async addWidget({state, commit, dispatch, rootState}, {widget, pageId}){
      // Fetch the Gridstack grid
      let grid = document.querySelector('#p'+pageId+'-grid');
      if (grid) {
        grid = grid.gridstack;
      } else {
        console.log('No grid found for page '+pageId);
        return false;
      }

      // Get the page we're updating
      let key = _.findKey(state.pages, {id: parseInt(pageId)}); // Parse in case string is passed via prop
      let page = state.pages[key];

      // Get the page's layout
      let layout = page.layout;
      // Set the widgets content to be the loading spinner
      if (widget.attributes.type &&
        (
          widget.attributes.type.includes(`chart`) ||
          widget.attributes.type === 'table' ||
          widget.attributes.type === 'stat-boxes'
        )
      ){
        widget.content = `<div id="${widget.id}"><div class="widget-loading"><i class="fa fa-btn fa-spinner fa-spin"></i></div></div>`;
      }

      // Check if the widget is using the report's colors, if so, update them.
      if (widget.attributes.options && widget.attributes.options.useColorsFrom === 'report') {
        widget.attributes.chartOptions.colors = rootState.report.report.colors;
      }
      // Add the widget to the page's layout
      layout.push(widget);

      // Update the stored page's layout
      await commit('updateLayout', {pageIndex: key, layout: layout});

      // Find the grid item and have gridstack turn it into a widget, then update its attributes.
      let gridItems = grid.getGridItems();
      _.each(gridItems, item => {
        if(item.attributes['gs-id'].value == widget.id){
          let gridWidget = grid.makeWidget(item);
          grid.update(gridWidget, widget);
        }
      });

      dispatch('savePages');
    },
    async updateWidget({state, dispatch, rootState}, {widget, pageId, preview = false}){
      // Fetch the Gridstack grid
      let grid = document.querySelector('#p'+pageId+'-grid');
      if (grid) {
        grid = grid.gridstack;
      } else {
        console.log('No grid found for page '+pageId);
        return false;
      }

      /**
       * TODO: This appears to be causing widgets to revert back to a loading state
       * When removed, the issue no longer occurs. Need to investigate.
       */
      /*await window.mixpanel('Report Builder: Edit report widget', {
        report_id: rootState.report.report.id,
        widget_id: widget.id
      });*/

      // Get the widget element.
      const widgetEl = document.querySelector(`[gs-id="${widget.id}"]`);
      // Check if the widget is using the report's colors, if so, update them.
      if (widget.attributes.options && widget.attributes.options.useColorsFrom === 'report') {
        widget.attributes.chartOptions.colors = rootState.report.report.colors;
      }

      // Find page and update the widget's attributes
      let key = _.findKey(state.pages, {id: parseInt(pageId)}); // Parse in case string is passed via prop
      let widgetKey = _.findKey(state.pages[key].layout, {id: widget.id});
      state.pages[key].layout[widgetKey] = widget;

      // Update the widget in Gridstack
      grid.update(widgetEl, widget);
      if (!preview) dispatch('savePages');
    },
    async deleteWidget({state, dispatch}, {widget, pageId}){
      // Remove the widget object from the stored page so it is removed from front end immediately.
      let pageKey = _.findKey(state.pages, function(o) {return o.id == pageId;});
      let page = state.pages[pageKey];
      let widgetKey = _.findKey(page.layout, function(o) {return o.id == widget.id;});
      page.layout.splice(widgetKey,1);

      // Fetch the Gridstack grid
      const grid = document.querySelector('#p'+pageId+'-grid').gridstack;
      // Get the widget element.
      const widgetEl = document.querySelector(`[gs-id="${widget.id}"]`);
      // Remove the widget from the grid
      grid.removeWidget(widgetEl);

      dispatch('savePages');
    },
    async deletePage({dispatch}, {pageId, reportId, type}){
      Swal.fire({
        icon: 'warning',
        title: 'Delete page',
        text: 'Are you sure you want to delete the page from your report?',
        showCancelButton: true,
        confirmButtonColor: "#DD6B55",
        confirmButtonText: 'Yes'
      }).then(async (result) => {
        if(result.isConfirmed){
          Swal.close();
          axios.post('/ajax/reports/builder/delete-page', {
              page_id: pageId
            })
            .then(() => {
              dispatch('getReportPages', { type, id: reportId });
            })
            .catch(axiosCatchHandler);
        }else{
          Swal.close();
        }
      });

    },
    debounceScalePageSize: _.debounce( function({dispatch}) {
      dispatch('scalePageSize');
    }, 50),
    async scalePageSize({state, dispatch}){
      return new Promise((resolve) => {
        // Grab and store the original page size
        if(state.pageWidthPx === 0){
          // Check if a page has been loaded, if not, queue this function to run again.
          if(!document.querySelector('.page')) {
            dispatch('debounceScalePageSize');
            return false;
          } else {
            state.pageWidthPx = document.querySelector('.page').clientWidth;
          }
        }
        // Identify the current with of the report section
        let containerWidth = document.getElementById('report_main').clientWidth;
        // Calculate the ratio and update the css root variable
        let ratio = containerWidth/state.pageWidthPx;
        let root = document.documentElement;
        root.style.setProperty('--percentage', (ratio > 1 ? 1 : ratio));
        resolve();
      });
    }
  },
  getters: {
    getPageByID:(state) => (id) => {
      let key = _.findKey(state.pages, {id: id});
      return state.pages[key];
    }
  }

};

export default pages;
